import React, { useState } from 'react';
import { useActivity } from '../App';
import { emailApi, notifAdminApi, usersApi } from '../api';
import toast from 'react-hot-toast';

const TYPE_COLORS = {
  system:     { bg: 'rgba(6,182,212,0.15)',  color: '#06b6d4' },
  feature:    { bg: 'rgba(99,102,241,0.15)', color: '#6366f1' },
  promo:      { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  security:   { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
  newsletter: { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
};

const NOTIF_TYPES = ['SYSTEM','BATTLE_INVITE','BADGE_EARNED','REFERRAL_BONUS','WITHDRAWAL_UPDATE','LEVEL_UP'];

const Notifications = () => {
  const { addLog } = useActivity();

  const [activeTab, setActiveTab] = useState('broadcast');

  // ── Broadcast ────────────────────────────────────────────────────────────
  const [broadcastForm, setBCForm] = useState({ title: '', message: '', type: 'SYSTEM' });
  const [bcSending, setBcSending]  = useState(false);
  const [bcResult,  setBcResult]   = useState(null);

  // ── Individual ───────────────────────────────────────────────────────────
  const [userSearch, setUserSearch]     = useState('');
  const [userResults, setUserResults]   = useState([]);
  const [searching, setSearching]       = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [indivForm, setIndivForm]       = useState({ title: '', message: '', type: 'SYSTEM' });
  const [indivSending, setIndivSending] = useState(false);

  // ── Email Blast ───────────────────────────────────────────────────────────
  const [emailForm, setEmailForm]     = useState({ title: '', body: '', versionTag: 'UPDATE', features: '' });
  const [adminSecret, setAdminSecret] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStats,   setEmailStats]   = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ── Drafts (session only) ─────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [draftModal, setDraftModal]       = useState(null);
  const [draftForm,  setDraftForm]        = useState({ title: '', message: '', type: 'system', recipients: 'All Users' });

  // ── Broadcast Handler ─────────────────────────────────────────────────────
  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.title || !broadcastForm.message) { toast.error('Title and message required'); return; }
    setBcSending(true); setBcResult(null);
    try {
      const res = await notifAdminApi.broadcast(broadcastForm);
      const d = res.data?.data;
      setBcResult(d);
      addLog('Broadcast Sent', broadcastForm.title, 'publish');
      toast.success(`Sent to ${d?.sent || 0} users!`);
      setBCForm({ title: '', message: '', type: 'SYSTEM' });
    } catch (err) { toast.error(err.response?.data?.message || 'Broadcast failed'); }
    finally { setBcSending(false); }
  };

  // ── User Search ───────────────────────────────────────────────────────────
  const handleUserSearch = async () => {
    if (!userSearch.trim()) return;
    setSearching(true); setUserResults([]);
    try {
      const res = await usersApi.getAll({ search: userSearch, size: 8, page: 0 });
      setUserResults(res.data?.data || []);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  // ── Individual Send ───────────────────────────────────────────────────────
  const handleSendIndividual = async (e) => {
    e.preventDefault();
    if (!selectedUser) { toast.error('Select a user first'); return; }
    if (!indivForm.title || !indivForm.message) { toast.error('Title and message required'); return; }
    setIndivSending(true);
    try {
      await notifAdminApi.sendToUser({ userId: selectedUser.id, ...indivForm });
      addLog('Individual Notification Sent', `${selectedUser.name}: ${indivForm.title}`, 'publish');
      toast.success(`Sent to ${selectedUser.name}!`);
      setIndivForm({ title: '', message: '', type: 'SYSTEM' });
      setSelectedUser(null); setUserSearch(''); setUserResults([]);
    } catch (err) { toast.error(err.response?.data?.message || 'Send failed'); }
    finally { setIndivSending(false); }
  };

  // ── Email Blast ───────────────────────────────────────────────────────────
  const handleSendEmailBlast = async (e) => {
    e.preventDefault();
    if (!emailForm.title || !emailForm.body) { toast.error('Title and body required'); return; }
    if (!adminSecret) { toast.error('Admin secret required'); return; }
    setEmailSending(true);
    try {
      const features = emailForm.features.split('\n').filter(f => f.trim());
      const res = await emailApi.sendUpdate(adminSecret, { ...emailForm, features });
      toast.success(res.data?.message || 'Email blast sent!');
      addLog('Email Blast Sent', emailForm.title, 'publish');
      setEmailForm({ title: '', body: '', versionTag: 'UPDATE', features: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Email blast failed'); }
    finally { setEmailSending(false); }
  };

  const loadEmailStats = async () => {
    if (!adminSecret) { toast.error('Enter admin secret first'); return; }
    setStatsLoading(true);
    try {
      const res = await emailApi.getStats(adminSecret);
      setEmailStats(res.data?.data);
    } catch { toast.error('Failed to load email stats'); }
    finally { setStatsLoading(false); }
  };

  // ── Draft Handlers ────────────────────────────────────────────────────────
  const handleSaveDraft = () => {
    if (!draftForm.title || !draftForm.message) { toast.error('Title and message required'); return; }
    if (draftModal === 'add') {
      setNotifications(p => [{ ...draftForm, id: Date.now(), status: 'draft', createdAt: new Date().toISOString() }, ...p]);
      toast.success('Draft saved!');
    } else {
      setNotifications(p => p.map(n => n.id === draftModal.id ? { ...n, ...draftForm } : n));
      toast.success('Draft updated!');
    }
    setDraftModal(null);
    setDraftForm({ title: '', message: '', type: 'system', recipients: 'All Users' });
  };

  const TABS = [
    { id: 'broadcast',  label: '📢 Broadcast'  },
    { id: 'individual', label: '👤 Individual'  },
    { id: 'email',      label: '📧 Email Blast' },
    { id: 'drafts',     label: '📋 Drafts'      },
  ];

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Content</span> / <span className="current">Notifications</span></div>
          <h1>Notifications</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Push notifications, email blasts and draft management</p>
        </div>
        <button style={primaryBtnS} onClick={() => { setActiveTab('drafts'); setDraftModal('add'); setDraftForm({ title: '', message: '', type: 'system', recipients: 'All Users' }); }}>
          ➕ New Draft
        </button>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 600, transition: 'all 0.2s',
              background: activeTab === t.id ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'var(--bg-tertiary)',
              color: activeTab === t.id ? 'white' : 'var(--text-secondary)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ BROADCAST ══════════════════ */}
      {activeTab === 'broadcast' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '4px' }}>📢 Broadcast to All Active Users</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Sends in-app + FCM push notification to every ACTIVE user.
            </p>
            <form onSubmit={handleBroadcast}>
              <div style={fgS}>
                <label style={labelS}>Title *</label>
                <input style={inputS} value={broadcastForm.title} onChange={e => setBCForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. New Feature Available!" required />
              </div>
              <div style={fgS}>
                <label style={labelS}>Notification Type</label>
                <select style={inputS} value={broadcastForm.type} onChange={e => setBCForm(p => ({ ...p, type: e.target.value }))}>
                  {NOTIF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fgS}>
                <label style={labelS}>Message *</label>
                <textarea style={{ ...inputS, minHeight: '90px', resize: 'vertical' }} value={broadcastForm.message} onChange={e => setBCForm(p => ({ ...p, message: e.target.value }))} placeholder="Write your message…" required />
              </div>
              <button type="submit" disabled={bcSending} style={{ ...primaryBtnS, width: '100%', justifyContent: 'center' }}>
                {bcSending ? '⏳ Sending to all users…' : '📢 Send Broadcast'}
              </button>
            </form>
          </div>

          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '16px' }}>📊 Result</h3>
            {bcResult ? (
              <div>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '3rem' }}>✅</div>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: '#22c55e', marginTop: '8px' }}>{bcResult.sent}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Users notified</div>
                </div>
                {[{ label: 'Total targeted', value: bcResult.total, color: 'var(--accent-primary)' },
                  { label: 'Successfully sent', value: bcResult.sent, color: '#22c55e' }].map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}>{s.label}</span>
                    <span style={{ fontWeight: 700, color: s.color }}>{s.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center', paddingTop: '40px' }}>Results will appear here after sending</p>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════ INDIVIDUAL ══════════════════ */}
      {activeTab === 'individual' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* User search */}
          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '4px' }}>👤 Find User</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '16px' }}>Search by name or email</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <input style={{ ...inputS, flex: 1 }} placeholder="Search name, email…" value={userSearch}
                onChange={e => setUserSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUserSearch()} />
              <button style={primaryBtnS} onClick={handleUserSearch} disabled={searching}>{searching ? '⏳' : '🔍'}</button>
            </div>

            {userResults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
                {userResults.map(u => (
                  <div key={u.id} onClick={() => { setSelectedUser(u); setUserResults([]); setUserSearch(u.name); }}
                    style={{ padding: '10px 12px', background: 'var(--bg-tertiary)', border: `1px solid ${selectedUser?.id === u.id ? '#6366f1' : 'var(--border-color)'}`,
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{u.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email} · {u.city || '—'}</div>
                  </div>
                ))}
              </div>
            )}

            {selectedUser && (
              <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px' }}>
                <div style={{ fontSize: '0.78rem', color: '#6366f1', fontWeight: 700, marginBottom: '4px' }}>✅ Selected</div>
                <div style={{ fontWeight: 700 }}>{selectedUser.name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{selectedUser.email} · ID: {selectedUser.id}</div>
                <button style={{ marginTop: '8px', fontSize: '0.75rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => { setSelectedUser(null); setUserSearch(''); }}>✕ Clear</button>
              </div>
            )}
          </div>

          {/* Message form */}
          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '4px' }}>✉️ Message</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              {selectedUser ? `→ Sending to: ${selectedUser.name}` : 'Select a user on the left first'}
            </p>
            <form onSubmit={handleSendIndividual}>
              <div style={fgS}>
                <label style={labelS}>Title *</label>
                <input style={inputS} value={indivForm.title} onChange={e => setIndivForm(p => ({ ...p, title: e.target.value }))} placeholder="Notification title" required />
              </div>
              <div style={fgS}>
                <label style={labelS}>Type</label>
                <select style={inputS} value={indivForm.type} onChange={e => setIndivForm(p => ({ ...p, type: e.target.value }))}>
                  {NOTIF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fgS}>
                <label style={labelS}>Message *</label>
                <textarea style={{ ...inputS, minHeight: '100px', resize: 'vertical' }} value={indivForm.message} onChange={e => setIndivForm(p => ({ ...p, message: e.target.value }))} placeholder="Write your message…" required />
              </div>
              <button type="submit" disabled={indivSending || !selectedUser}
                style={{ ...primaryBtnS, width: '100%', justifyContent: 'center', opacity: !selectedUser ? 0.5 : 1 }}>
                {indivSending ? '⏳ Sending…' : `📩 Send to ${selectedUser?.name || 'User'}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════ EMAIL BLAST ══════════════════ */}
      {activeTab === 'email' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px' }}>
          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '4px' }}>📧 Email Blast to All Users</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '20px' }}>Sends a rich HTML email to all ACTIVE users via the backend email service.</p>
            <form onSubmit={handleSendEmailBlast}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={fgS}><label style={labelS}>Email Title *</label><input style={inputS} value={emailForm.title} onChange={e => setEmailForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. New Feature Launch" required /></div>
                <div style={fgS}><label style={labelS}>Version Tag</label><input style={inputS} value={emailForm.versionTag} onChange={e => setEmailForm(p => ({ ...p, versionTag: e.target.value }))} placeholder="e.g. v2.1 UPDATE" /></div>
              </div>
              <div style={fgS}><label style={labelS}>Body *</label><textarea style={{ ...inputS, minHeight: '90px', resize: 'vertical' }} value={emailForm.body} onChange={e => setEmailForm(p => ({ ...p, body: e.target.value }))} placeholder="Main message content…" required /></div>
              <div style={fgS}><label style={labelS}>Feature List (one per line)</label><textarea style={{ ...inputS, minHeight: '70px', resize: 'vertical' }} value={emailForm.features} onChange={e => setEmailForm(p => ({ ...p, features: e.target.value }))} placeholder="New walk tracking&#10;Battle leaderboard" /></div>
              <div style={fgS}><label style={labelS}>Admin Secret (X-Admin-Secret)</label><input style={inputS} type="password" value={adminSecret} onChange={e => setAdminSecret(e.target.value)} placeholder="Set in application.properties" /></div>
              <button type="submit" disabled={emailSending} style={{ ...primaryBtnS, width: '100%', justifyContent: 'center' }}>
                {emailSending ? '⏳ Sending…' : '📧 Send Email Blast'}
              </button>
            </form>
          </div>

          <div style={cardS}>
            <h3 style={{ fontWeight: 700, marginBottom: '16px' }}>📊 Email Stats</h3>
            <div style={fgS}><label style={labelS}>Admin Secret</label><input style={inputS} type="password" value={adminSecret} onChange={e => setAdminSecret(e.target.value)} placeholder="Same secret" /></div>
            <button style={{ ...primaryBtnS, width: '100%', justifyContent: 'center', marginBottom: '16px' }} disabled={statsLoading} onClick={loadEmailStats}>
              {statsLoading ? 'Loading…' : '🔄 Load Stats'}
            </button>
            {emailStats ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[{ label: 'Total', value: emailStats.total, color: 'var(--accent-primary)' },
                  { label: 'Sent',  value: emailStats.sent,  color: '#22c55e' },
                  { label: 'Failed', value: emailStats.failed, color: '#ef4444' },
                  { label: 'Pending', value: emailStats.pending, color: '#f59e0b' }].map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}>{s.label}</span>
                    <span style={{ fontWeight: 700, color: s.color }}>{s.value}</span>
                  </div>
                ))}
              </div>
            ) : <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>Enter admin secret and click Load Stats</p>}
          </div>
        </div>
      )}

      {/* ══════════════════ DRAFTS ══════════════════ */}
      {activeTab === 'drafts' && (
        <>
          {notifications.length === 0 ? (
            <div style={{ ...cardS, textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📋</div>
              <div style={{ fontWeight: 700, marginBottom: '16px' }}>No drafts yet. Click "New Draft" to create one.</div>
              <button style={primaryBtnS} onClick={() => { setDraftModal('add'); setDraftForm({ title: '', message: '', type: 'system', recipients: 'All Users' }); }}>
                ➕ Create Draft
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
              {notifications.map(notif => {
                const c = TYPE_COLORS[notif.type] || TYPE_COLORS.system;
                return (
                  <div key={notif.id} style={{ ...cardS, borderLeft: `4px solid ${c.color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{notif.title}</div>
                        <span style={{ fontSize: '0.72rem', ...c, padding: '2px 8px', borderRadius: '20px', fontWeight: 600, marginTop: '4px', display: 'inline-block' }}>{notif.type}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={actionBtnS} onClick={() => { setDraftModal(notif); setDraftForm({ title: notif.title, message: notif.message, type: notif.type, recipients: notif.recipients }); }}>✏️</button>
                        <button style={{ ...actionBtnS, color: '#ef4444' }} onClick={() => { setNotifications(p => p.filter(n => n.id !== notif.id)); toast.success('Draft deleted'); }}>🗑️</button>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '10px' }}>{notif.message}</p>
                    <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>
                      👥 {notif.recipients} · 📅 {new Date(notif.createdAt).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Draft Modal ──────────────────────────────────────────────────────── */}
      {draftModal !== null && (
        <div style={overlayS}>
          <div style={{ ...modalS, maxWidth: '500px' }}>
            <div style={modalHeaderS}>
              <h3 style={{ fontWeight: 700 }}>{draftModal === 'add' ? '➕ New Draft' : '✏️ Edit Draft'}</h3>
              <button style={closeBtnS} onClick={() => setDraftModal(null)}>✕</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={fgS}><label style={labelS}>Title *</label><input style={inputS} value={draftForm.title} onChange={e => setDraftForm(p => ({ ...p, title: e.target.value }))} placeholder="Notification title" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={fgS}><label style={labelS}>Type</label><select style={inputS} value={draftForm.type} onChange={e => setDraftForm(p => ({ ...p, type: e.target.value }))}>{Object.keys(TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                <div style={fgS}><label style={labelS}>Recipients</label><select style={inputS} value={draftForm.recipients} onChange={e => setDraftForm(p => ({ ...p, recipients: e.target.value }))}>{['All Users', 'Active Users', 'Premium Users', 'New Users (7 days)'].map(r => <option key={r}>{r}</option>)}</select></div>
              </div>
              <div style={fgS}><label style={labelS}>Message *</label><textarea style={{ ...inputS, minHeight: '100px', resize: 'vertical' }} value={draftForm.message} onChange={e => setDraftForm(p => ({ ...p, message: e.target.value }))} placeholder="Notification content…" /></div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button style={cancelBtnS} onClick={() => setDraftModal(null)}>Cancel</button>
                <button style={primaryBtnS} onClick={handleSaveDraft}>Save Draft</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' };
const fgS        = { marginBottom: '14px' };
const labelS     = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const inputS     = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box' };
const primaryBtnS= { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '6px' };
const cancelBtnS = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS = { padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.78rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const overlayS   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS     = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS={ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS  = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };

export default Notifications;
